<template>
  <div class="relative x-captcha-input">
    <a-input
      type="text"
      :size="size"
      :class="{ 'ex-large-input': xLarge }"
      class="w-full"
      :value="value"
      :placeholder="placeholder"
      @change="event => emits('update:value', event.target.value)"
    />
    <a-button
      v-if="!isSend"
      class="absolute right-[0px] top-[0px]"
      :size="size"
      :class="{
        'ex-large-btn': xLarge,
      }"
      :type="type"
      @click="submit"
    >
      {{ $t('huo-qu-yan-zheng-ma') }}
    </a-button>
    <a-button
      v-else
      :type="type"
      class="absolute right-[0px] top-[0px]"
      :size="size"
      :class="{ 'ex-large-btn': xLarge }"
      :disabled="true"
    >
      {{ $t('zhong-xin-huo-qu-times-s', [times]) }}
    </a-button>
  </div>
</template>

<script lang="ts" setup>
import { getEmailCode, getRegisterCode } from '@/api';
import { useSendCode } from '@/hooks/useSendCode';
import { phoneReg } from '@/utils/form';

import { message } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';
import { emailReg } from '~/utils/regExp';
import { TRACK_EVENT, trackEvent } from '~/utils/track';

const { t: $t } = useI18n();

const { $isPure } = useNuxtApp();

const { sendCode, isSend, times } = useSendCode();
const emits = defineEmits(['update:value', 'submit']);
const props = withDefaults(
  defineProps<{
    value: string;
    placeholder?: string;
    data: string;
    size?: 'large' | 'middle' | 'small';
    isChecked?: boolean;
    currentData?: string;
    xLarge?: boolean;
    type?: 'primary' | 'link' | 'dashed' | 'text';
  }>(),
  {
    value: '',
    data: '',
    isChecked: true,
    type: 'link',
  },
);

const submit = async () => {
  if (!isGlobalVersion()) {
    if (!props.data || !phoneReg.test(props.data)) {
      message.warning($t('qing-shu-ru-zheng-que-de-shou-ji-hao'));
      return;
    }

    if (!props.isChecked && !$isPure) {
      message.warning($t('qing-xian-gou-xuan-fu-wu-gui-fan'));
      return;
    }

    if (props.currentData && props.currentData === props.data) {
      message.warning($t('qing-shu-ru-xin-de-shou-ji-hao'));
      return;
    }
    // 埋点：发送验证码
    const sendCodeStartTime = Date.now();
    trackEvent(TRACK_EVENT.LOGIN_SEND_VERIFY_CODE, {
      contact: props.data,
      type: 'phone',
    });

    const res = await getRegisterCode({
      mobile: props.data,
    });

    const sendCodeDuration = Date.now() - sendCodeStartTime;

    if (!res.code) {
      sendCode();
      // 埋点：发送验证码成功
      trackEvent(TRACK_EVENT.LOGIN_SEND_VERIFY_CODE_SUCCESS, {
        contact: props.data,
        type: 'phone',
        send_code_duration: sendCodeDuration,
      });
      message.success($t('yan-zheng-ma-fa-song-cheng-gong'));
    } else {
      // 埋点：发送验证码失败
      trackEvent(TRACK_EVENT.LOGIN_SEND_VERIFY_CODE_FAIL, {
        contact: props.data,
        type: 'phone',
        error_code: res.code,
        error_msg: res.msg || '发送失败',
        send_code_duration: sendCodeDuration,
      });
    }
  } else {
    if (!props.data || !emailReg.test(props.data)) {
      message.warning($t('qing-shu-ru-zheng-que-de-you-xiang'));
      return;
    }
    if (!props.isChecked && !$isPure) {
      message.warning($t('qing-xian-gou-xuan-fu-wu-gui-fan'));
      return;
    }

    if (props.currentData && props.currentData === props.data) {
      message.warning($t('qing-shu-ru-xin-de-you-xiang-di-zhi'));
      return;
    }
    // 埋点：发送验证码
    const sendCodeStartTime = Date.now();
    trackEvent(TRACK_EVENT.LOGIN_SEND_VERIFY_CODE, {
      contact: props.data,
      type: 'email',
    });

    const res = await getEmailCode({
      email: props.data,
    });

    const sendCodeDuration = Date.now() - sendCodeStartTime;

    if (!res.code) {
      sendCode();
      // 埋点：发送验证码成功
      trackEvent(TRACK_EVENT.LOGIN_SEND_VERIFY_CODE_SUCCESS, {
        contact: props.data,
        type: 'email',
        send_code_duration: sendCodeDuration,
      });
      message.success($t('yan-zheng-ma-fa-song-cheng-gong'));
    } else {
      // 埋点：发送验证码失败
      trackEvent(TRACK_EVENT.LOGIN_SEND_VERIFY_CODE_FAIL, {
        contact: props.data,
        type: 'email',
        error_code: res.code,
        error_msg: res.msg || '发送失败',
        send_code_duration: sendCodeDuration,
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.x-captcha-input {
  :deep(.ant-btn) {
    height: 22px;
    padding: 0 15px;
  }
  :deep(.ant-btn-lg) {
    height: 28px;
    padding: 0 15px;
  }
  :deep(.ex-large-input) {
    height: 44px;
    padding: 6px 15px;
    letter-spacing: 0.4px;
    box-sizing: border-box;
  }
  :deep(.ex-large-btn) {
    height: 36px;
    padding: 0 15px;
    box-sizing: border-box;
    top: 4px;
    right: 4px;
    border-radius: 6px;
    &:hover {
      color: var(--purple-6);
      background-color: var(--purple-1);
    }
    &:disabled {
      color: var(--purple-4);
      background-color: var(--purple-1);
    }
  }
}
</style>
